import React, { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useNavigationItems } from "@/domains/app/hooks/use-navigation-items";
import { useAppUIStore } from "@/domains/app/stores/app-ui-store";
import { useCurrentAbility } from "@/lib/authorization";
import { useAttentionCounts } from "@/lib/hooks/use-attention-counts";
import { useMobileAppLayout } from "@/lib/providers/mobile-app-layout-context";

const MobileTabBar: React.FC = () => {
  const tabBarRef = useRef<HTMLDivElement>(null);

  // Note: Removed notification count as notifications tab is no longer in mobile navigation
  const location = useLocation();

  // Get attention counts for dashboard badge
  const { total: attentionCount } = useAttentionCounts();

  // Get user and salon data for role-based navigation
  const ability = useCurrentAbility();

  // Use mobile app layout context to report height
  const { setTabBarHeight } = useMobileAppLayout();

  // Get mobile AI sheet state
  const mobileAiSheetOpen = useAppUIStore((state) => state.mobileAiSheetOpen);
  const toggleMobileAiSheet = useAppUIStore((state) => state.toggleMobileAiSheet);

  // Get navigation items based on user roles
  const { getMobileNavItems } = useNavigationItems();
  const navItems = getMobileNavItems();

  // Filter navigation items based on permissions
  const filteredNavItems = navItems.filter((item) => {
    // Always show items without permissions (like salon)
    if (!item.permission) return true;
    // Check ability for items with permissions
    return ability?.can(item.permission.action, item.permission.subject);
  });

  // Measure and report tab bar height
  useEffect(() => {
    const measureHeight = () => {
      if (tabBarRef.current) {
        const height = tabBarRef.current.offsetHeight;
        setTabBarHeight(height);
      }
    };

    // Measure on mount and when content changes
    measureHeight();

    // Set up ResizeObserver to handle dynamic height changes
    const resizeObserver = new ResizeObserver(measureHeight);
    if (tabBarRef.current) {
      resizeObserver.observe(tabBarRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [setTabBarHeight]);

  return (
    <div
      ref={tabBarRef}
      className="fixed left-0 right-0 bottom-0 border-t border-border md:hidden z-50 mobile-tab-bar-safe backdrop-blur-xl mobile-tab-5-layout"
    >
      <div className="flex justify-around items-center pt-2">
        {filteredNavItems.map((item) => {
          const Icon = item.icon;

          // AI Assistant tab opens sheet instead of navigation. The icon slot
          // matches other tabs (w-5 h-5 mb-1) so the label baseline stays
          // aligned; the gradient circle is absolutely positioned with its
          // center sitting on the tab bar's top separator line and carries a
          // pulsing glow halo to draw attention.
          if (item.to === "/ai-assistant") {
            return (
              <button
                type="button"
                key={item.to}
                onClick={toggleMobileAiSheet}
                className="flex flex-col items-center justify-center p-2 min-w-0 flex-1 relative"
              >
                <div className="relative w-5 h-3">
                  <div
                    className={`absolute left-1/2 -top-4 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-12 h-12 rounded-full text-white bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 transition-transform ${
                      mobileAiSheetOpen ? "ai-icon-glow-active scale-105" : "ai-icon-glow"
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
                <span
                  className={`text-xs font-medium text-center leading-tight ${
                    mobileAiSheetOpen ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          }

          // Regular navigation tabs
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => {
                // Special case for salon tab - should be active when in settings too
                // Special case for my-schedule tab - should be active when in appointments (if worker)
                const isActiveTab =
                  isActive ||
                  (item.to === "/salon" && location.pathname.startsWith("/settings")) ||
                  (item.to === "/my-schedule" && location.pathname.startsWith("/appointments"));

                return `flex flex-col items-center justify-center p-2 min-w-0 flex-1 relative ${
                  isActiveTab ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`;
              }}
            >
              <div className="relative">
                <Icon className="w-5 h-5 mb-1" />

                {/* Badge for attention count */}
                {item.badge?.({
                  unreadCount: 0,
                  attentionCount,
                })}
              </div>
              <span className="text-xs font-medium text-center leading-tight">{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
};

export default MobileTabBar;
